import { QueryKey as TQueryKey, useQuery } from '@tanstack/react-query';
import { createWorktype, getWorktypes, updateWorktype } from '../../services/worktype';
import { QueryKey } from '../../enums/QueryKey';
import { useCreateRecord, useUpdateRecord } from './crudOperations';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import { Worktype, WorktypeEditForm, WorktypeForm } from '../../types/Worktype.interface';
import { queryClient } from '../../services/queryClient';

export function useWorktypes({
    location,
    prependAllWorktypesOption,
    disabled = false,
}: {
    location?: number;
    prependAllWorktypesOption?: boolean;
    disabled?: boolean;
} = {}): [Worktype[], boolean] {
    const queryKey = location ? [QueryKey.Worktypes, String(location)] : ([QueryKey.Worktypes] as TQueryKey);

    const {
        data: worktypes,
        isLoading,
        isFetching,
        isFetchedAfterMount,
    } = useQuery<Worktype[], Error>({
        queryKey: queryKey as TQueryKey,
        queryFn: () => getWorktypes({ location }),
        initialData: [],
        enabled: !disabled,
        select: (data: Worktype[]) => {
            if (prependAllWorktypesOption && data && data.length && !data.find((item) => item.id === -1)) {
                data.unshift({
                    id: -1,
                    worktype: 'Alle Worktypes',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    forecasters: [],
                });
            }
            return data;
        },
    });

    return [worktypes, isFetchedAfterMount ? isLoading : isFetching];
}

export function useCreateWorktype({ onSettled }: UseMutationOptions<Worktype, Error, WorktypeForm>) {
    return useCreateRecord<Worktype, Error, WorktypeForm>({
        createRecordFn: createWorktype,
        fetchRecordsQueryKey: [QueryKey.Worktypes],
        recordName: 'Worktype',
        onSettled,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.Worktypes]);
        },
    });
}

export function useUpdateWorktype({ onSettled }: UseMutationOptions<Worktype, Error, WorktypeEditForm>) {
    return useUpdateRecord<Worktype, Error, WorktypeEditForm>({
        updateRecordFn: updateWorktype,
        fetchRecordsQueryKey: [QueryKey.Worktypes],
        recordName: 'Worktype',
        onSettled,
    });
}
